import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/ui-kit/utils';

const buttonVariants = cva(
  `focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:outline-destructive
  aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 inline-flex shrink-0
  cursor-pointer select-none items-center justify-center gap-2 whitespace-nowrap rounded-md
  text-center font-medium tracking-normal transition-all duration-150 focus-visible:ring-[3px]
  active:scale-[97%] disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0`,
  {
    variants: {
      variant: {
        contained: '',
        icon: '',
        text: 'bg-transparent outline-none',
        outlined: 'bg-transparent outline disabled:bg-transparent',
        ghost: 'hover:bg-accent bg-transparent outline-none', // alias to text
      },
      color: {
        primary: 'text-primary-foreground',
        'primary-muted': 'text-primary-foreground',
        success: 'text-success hover:text-success-foreground',
        danger: 'text-destructive hover:text-destructive-foreground',
      },
      size: {
        default: 'gap-2 rounded-sm px-4 py-2 text-sm leading-[16px]', // alias to md
        sm: 'gap-2 rounded-sm px-2 py-1 text-sm leading-[16px] has-[>svg]:px-2',
        md: 'text-md h-10 gap-2 rounded-sm px-8 py-3 leading-[22px]',
        lg: 'h-[40px] rounded-md px-8 py-3 text-lg leading-[22px] has-[>svg]:px-4',
        icon: 'size-6',
      },
    },
    compoundVariants: [
      {
        variant: 'icon',
        className: `text-foreground hover:text-foreground data-[active=true]:text-foreground
        disabled:text-muted-foreground bg-transparent p-0 disabled:bg-transparent`,
      },
      {
        color: 'primary',
        variant: 'contained',
        className: `bg-primary text-primary-foreground hover:bg-primary-hover data-[active=true]:bg-primary-hover
        disabled:bg-primary-disabled text-white disabled:text-white dark:disabled:text-[#7B8290]`,
      },
      {
        color: 'success',
        variant: 'contained',
        className: `text-success disabled:text-success-trans data-[active=true]:bg-success
        data-[active=true]:hover:!bg-success rounded-[10px] bg-[rgba(18,155,68,0.20)] hover:!bg-[#129B44]
        hover:!text-white data-[active=true]:text-gray-50 data-[active=true]:hover:!text-gray-50`,
      },
      {
        color: 'danger',
        variant: 'contained',
        className: `text-destructive disabled:text-destructive-trans rounded-[10px] bg-[rgba(225,55,54,0.20)]
        hover:!bg-[#E13736] hover:!text-white data-[active=true]:bg-red-500 data-[active=true]:text-gray-50
        data-[active=true]:hover:!bg-red-500 data-[active=true]:hover:!text-gray-50`,
      },

      // outlined
      {
        color: 'primary',
        variant: 'outlined',
        className: `outline-primary text-primary-foreground hover:text-primary data-[active=true]:text-primary
        disabled:outline-primary-disabled disabled:text-muted-foreground bg-transparent outline
        disabled:bg-transparent dark:disabled:text-[#7B7E93]`,
      },
      {
        color: 'primary-muted',
        variant: 'outlined',
        className: `outline-primary-muted text-primary-foreground hover:outline-primary data-[active=true]:text-primary
        disabled:outline-primary-disabled disabled:text-muted-foreground bg-transparent outline
        disabled:bg-transparent`,
      },
      {
        color: 'success',
        variant: 'outlined',
        className: `outline-success text-success-foreground hover:text-success data-[active=true]:text-success
        disabled:text-success-trans disabled:outline-success-trans bg-transparent outline`,
      },
      {
        color: 'danger',
        variant: 'outlined',
        className: `outline-destructive text-destructive-foreground hover:text-destructive
        data-[active=true]:text-destructive disabled:bg-destructive-trans disabled:text-destructive-trans
        bg-transparent`,
      },

      // text
      {
        color: 'primary',
        variant: ['text', 'ghost'],
        className: `text-primary-foreground hover:text-primary data-[active=true]:text-primary disabled:text-txt-3 p-1
        disabled:bg-transparent`,
      },
      {
        color: 'success',
        variant: 'text',
        className: `text-success hover:text-success data-[active=true]:text-success disabled:text-success-trans
        bg-transparent p-1`,
      },
      {
        color: 'danger',
        variant: 'text',
        className: `text-destructive hover:text-destructive data-[active=true]:text-destructive
        disabled:text-destructive-trans p-1`,
      },
    ],
    defaultVariants: {
      color: 'primary',
      variant: 'contained',
      size: 'md',
    },
  },
);

function Button({
  className,
  variant,
  color,
  size,
  active,
  loading = false,
  asChild = false,
  disableScale = false,
  disabled,
  children,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    active?: boolean;
    loading?: boolean;
    disableScale?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-active={active}
      data-slot="button"
      disabled={disabled || loading}
      className={cn(
        buttonVariants({ variant, color, size, className }),
        {
          'active:scale-100': disableScale,
          'btn-shimmer': loading,
        },
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  );
}

export { Button, buttonVariants };
